import { getMeetingStatus, groupMeetingsByStatus } from 'shared/meeting-utils';
import { fmtFullDate } from 'shared/dateutil';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { rpx } from 'client/lib/rpx-client';
import { useState } from 'preact/hooks';
import { AvatarStack } from '@components/avatars/avatar-stack';
import { showError } from '@components/app-error';
import type { CommunityMeetings, CommunityRsvpAvatars } from './types';

export function NextLiveCall({
  meetings,
  rsvpAvatars,
}: {
  meetings: CommunityMeetings;
  rsvpAvatars: CommunityRsvpAvatars;
}) {
  const { upcoming } = groupMeetingsByStatus(meetings);
  const nextMeeting = upcoming[0];

  if (!nextMeeting) {
    return undefined;
  }

  const status = getMeetingStatus(nextMeeting);
  const meetingUrl = `/courses/${AIP_COURSE_ID}/meetings/${nextMeeting.id}`;
  const isLive = status === 'inProgress' || status === 'aboutToStart';
  const showRsvp = nextMeeting.rsvpEnabled && !isLive;

  return (
    <div class="rounded-2xl p-5 mb-4 border border-indigo-200/60 dark:border-indigo-800/40 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-indigo-950/40 dark:via-gray-800 dark:to-violet-950/30 relative overflow-hidden">
      <div class="absolute top-4 right-4" style="opacity: 0.07">
        <svg
          class="text-indigo-600"
          fill="currentColor"
          viewBox="0 0 24 24"
          style="width: 80px; height: 80px"
        >
          <path d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
        </svg>
      </div>
      <div class="relative">
        <div class="flex items-center gap-2 mb-3">
          <span class="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 uppercase tracking-wider">
            <span
              class={`rounded-full animate-pulse ${isLive ? 'bg-red-500' : 'bg-indigo-400'}`}
              style="width: 8px; height: 8px"
            ></span>
            {isLive ? 'Live Now' : 'Next Live Call'}
          </span>
        </div>
        <a
          href={meetingUrl}
          class="text-base font-semibold text-gray-900 dark:text-white no-underline hover:text-indigo-700 transition"
        >
          {nextMeeting.title}
        </a>
        {nextMeeting.scheduledAt && (
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1.5 mb-2">
            <span class="inline-flex items-center gap-1.5">
              <svg
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="text-indigo-400"
                style="width: 16px; height: 16px; flex-shrink: 0"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                />
              </svg>
              {fmtFullDate(nextMeeting.scheduledAt)}
            </span>
          </p>
        )}
        {nextMeeting.description && (
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {nextMeeting.description
              .replace(/<[^>]*>/g, ' ')
              .replace(/&nbsp;/g, ' ')
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
              .replace(/\s+/g, ' ')
              .trim()}
          </p>
        )}

        {showRsvp && (
          <RsvpSection
            meetingId={nextMeeting.id}
            rsvpCount={nextMeeting.rsvpCount}
            hasRsvped={nextMeeting.hasRsvped}
            rsvpAvatars={rsvpAvatars}
          />
        )}

        {!showRsvp && (
          <a
            href={meetingUrl}
            class={`inline-flex items-center gap-1.5 text-sm font-medium no-underline transition ${
              isLive ? 'rounded-full px-5 py-1.5 text-white bg-indigo-600' : 'text-indigo-600'
            }`}
          >
            {isLive ? (
              'Join Call'
            ) : (
              <>
                View details & add to calendar
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2.5"
                  stroke="currentColor"
                  style="width: 14px; height: 14px; flex-shrink: 0"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
                  />
                </svg>
              </>
            )}
          </a>
        )}
      </div>
    </div>
  );
}

function RsvpSection({
  meetingId,
  rsvpCount: initialCount,
  hasRsvped: initialHasRsvped,
  rsvpAvatars,
}: {
  meetingId: UUID;
  rsvpCount: number;
  hasRsvped: boolean;
  rsvpAvatars: CommunityRsvpAvatars;
}) {
  const [hasRsvped, setHasRsvped] = useState(initialHasRsvped);
  const [count, setCount] = useState(initialCount);
  const [saving, setSaving] = useState(false);

  async function handleRsvp() {
    setSaving(true);
    try {
      if (hasRsvped) {
        await rpx.meetingRsvps.cancelRsvp({ meetingId });
        setHasRsvped(false);
        setCount((c) => c - 1);
      } else {
        await rpx.meetingRsvps.advanceRsvp({ meetingId });
        setHasRsvped(true);
        setCount((c) => c + 1);
      }
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="flex flex-col gap-3">
      {count > 0 && (
        <div class="flex items-center gap-2">
          <AvatarStack users={rsvpAvatars} totalCount={count} maxDisplay={5} size="size-6" />
          <span class="text-xs text-gray-500">
            {hasRsvped
              ? count === 1
                ? "You're going"
                : `You and ${count - 1} ${count - 1 === 1 ? 'other' : 'others'}`
              : `${count} attending`}
          </span>
        </div>
      )}
      <div class="flex items-center gap-3">
        {hasRsvped ? (
          <span class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
            <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            You're in!
          </span>
        ) : (
          <button
            type="button"
            disabled={saving}
            onClick={handleRsvp}
            class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition shadow-sm"
          >
            Save my spot!
          </button>
        )}
        {hasRsvped && (
          <button
            type="button"
            disabled={saving}
            onClick={handleRsvp}
            class="text-xs text-gray-400 hover:text-gray-600 transition"
          >
            Cancel RSVP
          </button>
        )}
      </div>
    </div>
  );
}
